import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/store/api/baseQuery';
import { YARDI_API_URL } from '@/constants/residents/api';

export interface YardiPaymentTransaction {
  id: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  paymentType: 'Credit Card' | 'ACH' | 'Check';
  referenceNumber: string;
  extTransactionId?: string;
  createdAt: Date;
  settledAt?: Date;
}

export interface YardiBalance {
  totalBalance: number;
  currentCharges: number;
  pastDueAmount: number;
  prepaidAmount: number;
  lastSyncedAt?: string | Date;
  details: Array<{
    chargeCode: string;
    description: string;
    amount: number;
    dueDate: Date;
    isPastDue: boolean;
  }>;
}

export interface YardiConfigStatus {
  propertyId: number;
  isYardiEnabled: boolean;
  hasCredentials: boolean;
  pmsType: string;
  yardiEndpoint?: string | null;
}

export const yardiPaymentApi = createApi({
  reducerPath: 'yardiPaymentApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['YardiBalance', 'YardiTransaction', 'YardiConfig'],
  endpoints: (builder) => ({
    // Sync balance from Yardi
    syncYardiBalance: builder.mutation<YardiBalance, { leaseId: string }>({
      query: (body) => ({
        url: `${YARDI_API_URL}/sync-balance`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.response as YardiBalance,
      invalidatesTags: ['YardiBalance'],
    }),

    // Get current balance (performs sync and returns latest)
    getYardiBalance: builder.query<YardiBalance, string>({
      query: (leaseId) => ({
        url: `${YARDI_API_URL}/sync-balance`,
        method: 'POST',
        body: { leaseId },
      }),
      transformResponse: (response: any) => response?.response as YardiBalance,
      providesTags: ['YardiBalance'],
    }),

    // Import transaction to Yardi
    importTransaction: builder.mutation<
      { paymentId: string; extTransactionId: string; status: string },
      { paymentId: string }
    >({
      query: (body) => ({
        url: `${YARDI_API_URL}/import-transaction`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.response,
      invalidatesTags: ['YardiTransaction'],
    }),

    // Get transaction status
    getTransactionStatus: builder.query<
      { status: string; extTransactionId?: string; errorMessage?: string; lastUpdated?: string },
      string
    >({
      query: (paymentId) => `${YARDI_API_URL}/transaction-status/${paymentId}`,
      transformResponse: (response: any) => response?.response,
      providesTags: ['YardiTransaction'],
    }),

    // Process batch deposit
    processBatchDeposit: builder.mutation<
      { totalPayments: number; results: any[] },
      {
        paymentIds: string[];
        depositDate?: string;
        depositMemo?: string;
      }
    >({
      query: (body) => ({
        url: `${YARDI_API_URL}/batch-deposit`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.response,
      invalidatesTags: ['YardiTransaction'],
    }),

    // Get Yardi configuration status
    getConfigurationStatus: builder.query<YardiConfigStatus, number>({
      query: (propertyId) => ({
        url: `${YARDI_API_URL}/configuration-status`,
        params: { propertyId },
      }),
      transformResponse: (response: any) => response?.response as YardiConfigStatus,
      providesTags: ['YardiConfig'],
    }),

    // Test Yardi connection
    testConnection: builder.mutation<
      { connected: boolean; message?: string; error?: string },
      number
    >({
      query: (propertyId) => ({
        url: `${YARDI_API_URL}/test-connection`,
        method: 'POST',
        params: { propertyId },
      }),
      transformResponse: (response: any) => response?.response,
    }),
  }),
});

export const {
  useSyncYardiBalanceMutation,
  useGetYardiBalanceQuery,
  useImportTransactionMutation,
  useGetTransactionStatusQuery,
  useLazyGetTransactionStatusQuery,
  useProcessBatchDepositMutation,
  useGetConfigurationStatusQuery,
  useTestConnectionMutation,
} = yardiPaymentApi;